import { useGetAnalyticsSla } from '@agos/api-client/customers'
import { useGetSalesChannels } from '@agos/api-client/orders'
import {
  channelLabel,
  filterChannelsOf,
  T, formatCount, formatDecimal, tt } from '@agos/shared'
import { ButtonGroup, Spinner, cn } from '@agos/ui'
import { AlertTriangle, Clock, Zap } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  hourBars,
  initialPeriod,
  isoDay,
  periodLabel,
  slaParams,
  slaTimings,
  slaWindowLabel,
  type Period,
  type PresetDays,
  type SlaResponse,
} from './analytics-report'

/**
 * The *Operácie* tab — whether orders go out on time.
 *
 * **FB-116, both halves.**
 *
 * The range parameters here are **`dateFrom` / `dateTo`**, not the `from` / `to` every other endpoint
 * on this page uses. Sending the wrong pair does not fail: the parameter is ignored and the endpoint
 * answers over its 30-day default. Measured — `?from=2026-06-01&to=2026-06-30` gives **2 366** orders,
 * `?dateFrom=…&dateTo=…` gives **2 625** for June. So the card reads its window back off the response's
 * own `dateFrom` / `dateTo` / `windowDays` rather than trusting what it asked for.
 *
 * And the average is contaminated. The payload carries `min_minutes` and `max_minutes`, which legacy
 * drops: the restaurant channel's fastest completed order is **0,06 minutes — under four seconds** from
 * *new* to *delivered*, which is a till transaction closed on the spot and not a meal that was cooked.
 * The median and the p95 shrug that off; the average does not, and legacy shows the average first.
 */

const OPS_PRESETS: readonly PresetDays[] = [7, 30, 90]

/**
 * Read only when `/sales-channels` has not answered yet — §9.121. The franchise's own names win, and
 * they must: the owner had already renamed `bar` to *Osobný odber* and this map went on saying *Bar*.
 */
const CHANNEL_FALLBACKS: Record<string, string> = {
  delivery: tt('Rozvoz'),
  restaurant: tt('Reštaurácia'),
  bar: tt('Osobný odber'),
  na_sekeru: tt('Na sekeru'),
}

export function OpsTab() {
  const { data: channelData } = useGetSalesChannels()
  const channels = filterChannelsOf(channelData)

  const [today] = useState(() => new Date())
  const [period, setPeriod] = useState<Period>(() => ({ ...initialPeriod(today), days: 30 }))

  const { data, isLoading } = useGetAnalyticsSla(slaParams(period))
  const response = (data ?? {}) as SlaResponse
  const timings = useMemo(() => slaTimings(response.byChannel ?? []), [response.byChannel])
  const { bars, peak } = useMemo(() => hourBars(response.byHour ?? []), [response.byHour])
  const worstMinutes = bars.reduce((worst, bar) => Math.max(worst, bar.p95Minutes), 0)
  const contaminated = timings.filter((timing) => timing.averageIsContaminated)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap" data-ops-period>
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <T>Obdobie</T>
        </span>
        <ButtonGroup
          options={OPS_PRESETS.map((days) => ({ value: String(days), label: `${days} ${tt('dní')}` }))}
          value={period.kind === 'preset' ? String(period.days) : ''}
          onChange={(value) =>
            setPeriod((current) => ({ ...current, kind: 'preset', days: Number(value) as PresetDays }))
          }
        />
        <button
          type="button"
          onClick={() =>
            setPeriod((current) => ({
              ...current,
              kind: current.kind === 'custom' ? 'preset' : 'custom',
            }))
          }
          aria-pressed={period.kind === 'custom'}
          className={cn(
            'px-3 py-1.5 rounded-lg text-xs font-bold',
            period.kind === 'custom'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/50 text-muted-foreground hover:bg-muted',
          )}
        >
          <T>Vlastný rozsah</T>
        </button>
        {period.kind === 'custom' && (
          <div className="flex items-center gap-2 pl-2 border-l border-border">
            <input
              type="date"
              value={period.from}
              max={period.to}
              aria-label={tt('Od dátumu')}
              onChange={(event) => setPeriod((current) => ({ ...current, from: event.target.value }))}
              className="px-2 py-1 rounded-md border border-border bg-background text-xs"
            />
            <span className="text-xs text-muted-foreground">→</span>
            <input
              type="date"
              value={period.to}
              min={period.from}
              max={isoDay(today)}
              aria-label={tt('Do dátumu')}
              onChange={(event) => setPeriod((current) => ({ ...current, to: event.target.value }))}
              className="px-2 py-1 rounded-md border border-border bg-background text-xs"
            />
          </div>
        )}
        <span className="ml-auto text-[11px] text-muted-foreground italic" data-ops-period-label>
          {/* What the server says it measured, not what was asked - the parameter names differ here. */}
          {slaWindowLabel(response)}
          {periodLabel(period) !== slaWindowLabel(response) && (
            <span className="ml-1 opacity-60">({periodLabel(period)})</span>
          )}
        </span>
      </div>

      <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground space-y-1">
        <p className="font-bold text-foreground">
          <T>Čo tie čísla znamenajú</T>
        </p>
        <p>
          <strong className="text-foreground">
            <T>Priemer</T>
          </strong>{' '}
          <T>- aritmetický priemer. Jedna extrémna objednávka ho vie posunúť.</T>
        </p>
        <p>
          <strong className="text-foreground">
            <T>Medián (p50)</T>
          </strong>{' '}
          <T>- polovica objednávok bola rýchlejšia, polovica pomalšia. Bežná skúsenosť zákazníka.</T>
        </p>
        <p>
          <strong className="text-foreground">p95</strong>{' '}
          <T>
            - 95 % objednávok bolo hotových do tohto času. Tých zvyšných 5 % píše zlé recenzie, takže
            zlepšenie p95 má najväčší dopad.
          </T>
        </p>
      </div>

      <div className="bg-card rounded-2xl border border-border p-5" data-sla-channels>
        <h3 className="font-bold mb-1 flex items-center gap-2">
          <Zap className="w-4 h-4 text-primary" /> <T>Časy podľa kanála</T>
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          <T>Od prijatia po doručenie alebo vyzdvihnutie</T>
        </p>

        {contaminated.length > 0 && (
          <div
            className="rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 mb-3 text-xs space-y-1"
            data-average-contaminated
          >
            {/* FB-116's second half. The evidence is in the payload; legacy never shows it. */}
            <p className="font-bold text-amber-500 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              <T>Priemer je skreslený v</T> {formatCount(contaminated.length)}{' '}
              {contaminated.length === 1 ? <T>kanáli</T> : <T>kanáloch</T>}
            </p>
            <p className="text-muted-foreground">
              <T>
                Najrýchlejšia „hotová“ objednávka trvala
              </T>{' '}
              {contaminated
                .map(
                  (timing) =>
                    `${channelLabel(timing.type, channels, CHANNEL_FALLBACKS)} ${formatSeconds(timing.minMinutes)}`,
                )
                .join(' · ')}
              .{' '}
              <T>
                Toľko sa nedá pripraviť jedlo - je to účet zavretý na kase v momente zaplatenia.
                Medián a p95 to neovplyvní, priemer áno. Preto je minimum aj maximum na tejto karte.
              </T>
            </p>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : timings.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            <T>Nie sú dostupné dáta o ukončených objednávkach.</T>
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {timings.map((timing) => (
              <div
                key={timing.type}
                className="rounded-xl border border-border p-4"
                data-sla-channel={timing.type}
                data-contaminated={timing.averageIsContaminated ? 'yes' : 'no'}
              >
                <h4 className="font-bold mb-2">{channelLabel(timing.type, channels, CHANNEL_FALLBACKS)}</h4>
                <dl className="space-y-1.5 text-xs">
                  <Row label={tt('Objednávok')} value={formatCount(timing.count)} />
                  <Row
                    label={tt('Priemer')}
                    value={`${formatDecimal(timing.avgMinutes, 0)} min`}
                    tone={timing.averageIsContaminated ? 'text-amber-500' : undefined}
                  />
                  <Row label={tt('Medián (p50)')} value={`${formatDecimal(timing.p50Minutes, 0)} min`} />
                  <Row
                    label={tt('p95 (najpomalších 5 %)')}
                    value={`${formatDecimal(timing.p95Minutes, 0)} min`}
                    tone="text-orange-500"
                  />
                  {/* Both in the payload, both dropped by legacy. */}
                  <Row
                    label={tt('Najrýchlejšia')}
                    value={formatSeconds(timing.minMinutes)}
                    tone={timing.averageIsContaminated ? 'text-amber-500' : undefined}
                  />
                  <Row label={tt('Najpomalšia')} value={`${formatDecimal(timing.maxMinutes, 0)} min`} />
                </dl>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-card rounded-2xl border border-border p-5" data-sla-hours>
        <h3 className="font-bold mb-1 flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" /> <T>Časy podľa hodiny</T>
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          <T>Kedy systém spomaľuje.</T>{' '}
          {peak !== null && (
            <>
              <T>Najhoršie p95 je o</T> <strong>{peak.hour}:00</strong> -{' '}
              <strong>{formatDecimal(peak.p95Minutes, 0)} min</strong> <T>pri</T>{' '}
              {formatCount(peak.count)} <T>objednávkach</T>.
            </>
          )}
        </p>

        <div className="flex items-center gap-4 mb-3 flex-wrap text-xs">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-blue-500" />
            <T>Priemer</T>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-orange-500" />
            p95
          </span>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : bars.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            <T>Nie sú dostupné dáta.</T>
          </p>
        ) : (
          <>
            {/* Thirteen hours come back, not twenty-four: the shop is shut for the rest, and drawing
                eleven empty columns would suggest it was open and idle. */}
            <div className="flex items-end gap-2 h-52" data-hour-bars>
              {bars.map((bar) => (
                <div key={bar.hour} className="flex-1 flex flex-col items-center gap-1 h-full">
                  <div className="flex-1 w-full flex items-end justify-center gap-0.5">
                    <div
                      data-hour-avg={bar.hour}
                      title={`${bar.hour}:00 - ${tt('priemer')} ${formatDecimal(bar.avgMinutes, 0)} min`}
                      className="w-1/2 bg-blue-500 rounded-t"
                      style={{ height: `${worstMinutes === 0 ? 0 : (bar.avgMinutes / worstMinutes) * 100}%` }}
                    />
                    <div
                      data-hour-p95={bar.hour}
                      title={`${bar.hour}:00 - p95 ${formatDecimal(bar.p95Minutes, 0)} min`}
                      className="w-1/2 bg-orange-500 rounded-t"
                      style={{ height: `${worstMinutes === 0 ? 0 : (bar.p95Minutes / worstMinutes) * 100}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground tabular-nums">{bar.hour}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              <T>Server vrátil</T> {formatCount(bars.length)}{' '}
              <T>hodín - počas ostatných je zatvorené.</T>
            </p>
          </>
        )}
      </div>
    </div>
  )
}

function Row({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn('font-bold tabular-nums', tone)}>{value}</dd>
    </div>
  )
}

/** Under a minute, minutes are the wrong unit — and the point is how small it is. */
function formatSeconds(minutes: number): string {
  if (minutes >= 1) return `${formatDecimal(minutes, 1)} min`
  return `${Math.round(minutes * 60)} s`
}
